import MiniCADAPI from "./MiniCADAPI";
import azure from "azure-storage";
import Project from "./project";
import { immerable } from "immer";

/**
 * SubProject data access layer class.
 */
export default class SubProject{
    [immerable] = true;

    /**
     * Sub Project constructor
     * @param id {string} - SubProjectId
     * @param parentProjectId {string} - projectId
     * @param projectName {String}
     * @param engineer {String}
     * @param city {String}
     * @param state {String}
     * @param country {String}
     * @param region {String}
     * @param createdDate {Date}
     * @param statusDate {Date}
     * @param editedDate {Date}
     * @param processingStatus {SubProjectService.PROCESSING_STATUS}
     * @param bomFileUrl {String}
     * @param csvFileUrl
     * @param stageStorageURL
     * @param multiBedStageStorageURL
     * @param active {Boolean}
     * @param workItemId {String}
     * @param inputFileURL {String}
     * @param outputFileURL {String}
     * @param projectType {String}
     */
    constructor(
        id = null,
        parentProjectId = null,
        projectName = null,
        engineer = null,
        city = null,
        state = null,
        country = null,
        region = null,
        createdDate = new Date(),
        statusDate = new Date(),
        editedDate = null,
        processingStatus = Project["PROCESSING_STATUS"].unsubmitted,
        bomFileUrl = null,
        active = true,
        workItemId = "",
        inputFileURL = "",
        outputFileURL = "",
        projectType = null,
        csvFileUrl = null,
        stageStorageURL= null,
        multiBedStageStorageURL= null,
    ) {
        /* class properties */
        this.id = id;
        this.parentProjectId = parentProjectId;
        this.projectName = projectName;
        this.engineer = engineer;
        this.city = city;
        this.state = state;
        this.country = country;
        this.region = region;
        this.createdDate = createdDate;
        this.statusDate = statusDate;
        this.editedDate = editedDate;
        this.processingStatus = processingStatus;
        this.bomFileUrl = bomFileUrl;
        this.active = active;
        this.workItemId = workItemId;
        this.inputFileURL = inputFileURL;
        this.outputFileURL = outputFileURL;
        this.projectType = projectType;
        this.csvFileUrl = csvFileUrl;
        this.stageStorageURL = stageStorageURL;
        this.multiBedStageStorageURL = multiBedStageStorageURL;
    }

    /**
     * SubProject Object de-serializer
     * @param {Object} record The object form Azure storage table.
     * @returns {SubProject}
     * @private
     */
    static _deserializeFromEntity( record ) {
        const isPipe = record.projectType._ === "PIPE";

        return new SubProject(
            record.RowKey._,
            record.parentProjectId._,
            record.projectName._,
            record.engineer._,
            record.city._,
            record.state._,
            record.country ? record.country._ : "",
            record.region ? record.region._ : "",
            record.createdDate._,
            record.statusDate._,
            ( record.editedDate ) ? record.editedDate._ : null,
            record.processingStatus._,
            ( isPipe && record.bomFileUrlPipe ) ? record.bomFileUrlPipe._ : ( !isPipe && record.bomFileUrl ) ? record.bomFileUrl._ : "",
            record.active._,
            ( record.workItemId ) ? record.workItemId._ : "",
            ( record.inputFileURL ) ? record.inputFileURL._ : "",
            ( isPipe && record.outputFileURLPipe ) ? record.outputFileURLPipe._ : ( !isPipe && record.outputFileURL ) ? record.outputFileURL._ : "",
            record.projectType._,
            ( isPipe && record.csvFileUrlPipe ) ? record.csvFileUrlPipe._ : ( !isPipe && record.csvFileUrl ) ? record.csvFileUrl._ : "",
            ( isPipe ) ? null : ( !isPipe && record.stageStorageURL ) ? record.stageStorageURL._ : "",
            ( isPipe ) ? null : ( !isPipe && record.multiBedStageStorageURL ) ? record.multiBedStageStorageURL._ : "",
        );
    }

    /**
     * SubProject Object serializer
     * @param project {SubProject} - The object form Azure storage table.
     * @param oid {String}
     * @returns {*}
     * @private
     */
    static _serializeToEntity( project, oid ) {
        try {
            const entGen = azure.TableUtilities.entityGenerator;
            return {
                PartitionKey: entGen.String(oid),
                RowKey: entGen.String(project.id),
                parentProjectId: entGen.String(project.parentProjectId),
                projectName: entGen.String(project.projectName),
                engineer: entGen.String(project.engineer),
                city: entGen.String(project.city),
                state: entGen.String(project.state),
                country: entGen.String(project.country),
                region: entGen.String(project.region),
                createdDate: entGen.DateTime(project.createdDate),
                statusDate: entGen.DateTime(project.statusDate),
                editedDate: entGen.DateTime(project.editedDate),
                processingStatus: entGen.String(project.processingStatus),
                bomFileUrl: entGen.String(project.bomFileUrl),
                active: entGen.Boolean(project.active),
                workItemId: entGen.String(project.workItemId),
                inputFileURL: entGen.String(project.inputFileURL),
                outputFileURL: entGen.String(project.outputFileURL),
                projectType: entGen.String(project.projectType),
                csvFileUrl: entGen.String(project.csvFileUrl),
                stageStorageURL: entGen.String(project.stageStorageURL),
                multiBedStageStorageURL: entGen.String(project.multiBedStageStorageURL),
            };
        } catch (error) {
            console.error("SubProject._serializeToEntity() :: ", error.message);
        }
    }

    /**
     * This function fetches sub project records from backend table storage.
     * @param oid {String} - Unique key used in table storage, this is the user's iod.
     * @param projectId {String} - ParentId or project Id.
     * @param tableToken {String} - The SAS access token in order to access the table.
     * @returns {Array<SubProject>}
     */
    static async fetchSubProjects( oid, projectId, tableToken ) {
        let records = await MiniCADAPI.getSubProjectRecords(oid, projectId, tableToken);
        return records.map(SubProject["_deserializeFromEntity"]);
    }

    static async fetchUserSubProjects( oid, tableToken ) {
        let records = await MiniCADAPI.getUserSubProjectRecords(oid, tableToken);
        return records.map(SubProject["_deserializeFromEntity"]);
    }

    /**
     * This function fetches project records from backend table storage.
     * @param oid {String} - Unique key used in table storage, this is the user's iod.
     * @param tableToken {String} - The SAS access token in order to access the table.
     * @returns {Promise<void>}
     */
    async persist( oid, tableToken ) {
        if (!this.country) {
            console.info("sp country missing");
            if (this.region === "Canada") {
                this.country = this.region;
                console.log("sp country corrected:", this.country);
            }
        }

        if (!this.region) console.error("sp region missing");

        if (!this.city) console.error("sp city missing");

        try {
            if (this._invalid()) {
                const thisMsg = JSON.stringify(this, null, 2);
                const msg = `Some required fields are not present, please check object consistency before saving: ${thisMsg}`;
                throw new Error(msg);
            }
            this.editedDate = new Date();
            let entity = SubProject._serializeToEntity(this, oid);
            return await MiniCADAPI.insertOrReplaceSubProject(entity, tableToken);
        } catch (error) {
            console.error("SubProject.persist() :: ", error.message);
        }
    }

    _invalid() {
        if (this["region"] === "USA") {
            return [
                "id",
                "parentProjectId",
                "projectName",
                "engineer",
                "city",
                "state",
                "projectType",
                "region",
            ].some(( prop ) => {
                return !this[prop];
            });
        } else {
            return [
                "id",
                "parentProjectId",
                "projectName",
                "engineer",
                "city",
                "country",
                "projectType",
                "region",
            ].some(( prop ) => {
                return !this[prop];
            });
        }
    }

    /**
     * This function fetches *this* project record from backend table storage.
     * @param oid {String} - Unique key used in table storage, this is the user's iod.
     * @param tableToken {String} - The SAS access token in order to access the table.
     * @returns {Promise<SubProject>}
     */
    async fetch( oid, tableToken ) {
        try {
            let entity = await MiniCADAPI
                .getSubProjectRecord(oid, ( this.id ).toString(), tableToken);

            if (!entity)
                throw new Error(`Project with id = '${this.id}' and partition key ='${oid}' not found.`);

            let subProject = SubProject._deserializeFromEntity(entity);
            Object.assign(this, subProject);
            return subProject;
        } catch (error) {
            console.error(`SubProject.fetch() :: ${error.message}`);
        }
    }

    /**
     * This function fetch project state from blob storage.
     * @param oid {String} - Unique key used in blob storage, this is the user's oid.
     * @param blobToken {String} - The SAS access token in order to access the blob.
     * @returns {Promise<void>}
     */
    async fetchState( oid, blobToken ) {
        try {
            return await MiniCADAPI
                .getSubProjectFile(
                    oid,
                    `${this.id}-sp-state`,
                    blobToken,
                );
        } catch (error) {
            console.error(`SubProject.fetchState() :: ${error.message}`);
        }
    }

    async fetchAutomation( oid, blobToken ) {
        try {
            return await MiniCADAPI
                .getSubProjectFile(
                    oid,
                    `${this.id}-automation`,
                    blobToken,
                );
        } catch (error) {
            console.error(`SubProject.fetchAutomation() :: ${error.message}`);
        }
    }

    /**
     * This function persist project state to blob storage.
     * @param state {Object} - All data required to return the project to last saved state.
     * @param oid {String} - Unique key used in blob storage, this is the user's iod.
     * @param blobToken {String} - The SAS access token in order to access the blob.
     * @param progressFN {Function} - The progress function to report back upload progress.
     * @returns {Promise<void>}
     */
    async persistState( state, oid, blobToken, progressFN ) {
        await MiniCADAPI
            .putSubProjectFile(
                oid,
                `${this.id}-sp-state`,
                state,
                blobToken,
                progressFN,
            );
    }

    /**
     * This function persist project state to blob storage.
     * @param state {Object} - All data required to return the project to last saved state.
     * @param oid {String} - Unique key used in blob storage, this is the user's iod.
     * @param blobToken {String} - The SAS access token in order to access the blob.
     * @param progressFN {Function} - Progress function to report back upload progress.
     * @returns {Promise<void>}
     */
    async persistAutomationState( state, oid, blobToken, progressFN ) {
        await MiniCADAPI
            .putProjectFile(
                oid,
                `${this.id}-automation`,
                state,
                blobToken,
                progressFN,
            );
    }

    async fetchAutomationState( oid, blobToken ) {
        const result = await MiniCADAPI.getSubProjectFile(
            oid,
            `${this.id}-automation`,
            blobToken,
        );
        return result;
    }

    /**
     * This function submit project automation task to forge via Azure.
     * @param oid {String} - Unique key used in blob storage, this is the user's iod.
     * @param idToken {String} - Token id.
     * @param email {String}
     * @param accessToken {String}
     * @returns {Promise<void>}
     */


    /**
     * This function submit project automation task to forge via Azure.
     * @param oid {String} - Unique key used in blob storage, this is the user's iod.
     * @returns {Promise<string>}
     */
    async checkWorkItemStatus( oid ) {
        console.log("checking for", this.workItemId);
        return await MiniCADAPI
            .getTaskStatus(
                this.workItemId,
                oid,
                this.id,
            );
    }
}



// WEBPACK FOOTER //
// ./src/lib/sub-project.js